
import React, { useState } from 'react';
// Corrected import path for types.ts
import { Article, Attachment, Sector } from '@/types'; // Corrected import path
import { ChevronRight, Download, FileText, Calendar, ArrowLeft, Paperclip, Eye, X, File, Image as ImageIcon, ExternalLink, ShieldCheck, Globe, Music, Video, FileSpreadsheet } from 'lucide-react';

interface ArticleViewProps {
  article: Article;
  onBack: () => void;
  onNavigateHome: () => void;
  onNavigateSector: (sector: Sector | null) => void; // Modified to accept selectedSector
}

export const ArticleView: React.FC<ArticleViewProps> = ({ article, onBack, onNavigateHome, onNavigateSector }) => {
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isInternalFile = (url: string) => url.includes('firebasestorage.googleapis.com') || url.startsWith('data:');

  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    if (type.includes('image')) return <ImageIcon className="w-8 h-8 text-blue-500" />;
    if (type.includes('audio')) return <Music className="w-8 h-8 text-orange-500" />;
    if (type.includes('video')) return <Video className="w-8 h-8 text-purple-500" />;
    if (type.includes('spreadsheet') || type.includes('excel')) return <FileSpreadsheet className="w-8 h-8 text-green-600" />;
    if (type.includes('word') || type.includes('document')) return <FileText className="w-8 h-8 text-blue-600" />;
    return <File className="w-8 h-8 text-slate-500" />;
  };

  const handleDownload = (e: React.MouseEvent, att: Attachment) => {
    e.stopPropagation();
    if (att.url.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = att.url;
        link.download = att.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } else {
        window.open(att.url, '_blank');
    }
  };

  // Helper function to generate viewer URL
  const getViewerUrl = (att: Attachment) => {
      // Se for Base64 (interno do banco antigo), usa direto
      if (att.url.startsWith('data:')) return att.url;
      
      // Se for link externo (WordPress), usa o Google Docs Viewer para renderizar
      // Isso é para compatibilidade com arquivos OFFICE que foram migrados do WordPress
      return `https://docs.google.com/gview?url=${encodeURIComponent(att.url)}&embedded=true`;
  };

  const isOfficeDoc = (type: string) => {
      return type.includes('word') || type.includes('document') || 
             type.includes('spreadsheet') || type.includes('excel') ||
             type.includes('powerpoint') || type.includes('presentation');
  };

  // Função auxiliar para renderizar texto com negrito (**texto**)
  const formatText = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-black text-slate-900">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="bg-white min-h-full rounded-3xl shadow-sm border border-slate-200 animate-fade-in flex flex-col relative overflow-hidden">
      
      <div className="p-10 pb-0">
        <div className="flex items-center gap-4 mb-8">
           <button onClick={onBack} className="p-3 rounded-2xl bg-slate-50 hover:bg-slate-100 text-slate-500 transition-all"><ArrowLeft className="w-5 h-5" /></button>
           <nav className="flex items-center text-xs font-black uppercase tracking-widest text-slate-400">
            <button onClick={onNavigateHome} className="hover:text-blue-600">Documentos</button>
            <ChevronRight className="w-4 h-4 mx-2 opacity-50" />
            <button 
              onClick={() => onNavigateSector({id: article.sectorId, name: article.sectorName, parentId: null})} 
              className="hover:text-blue-600"
            >
              {article.sectorName}
            </button>
          </nav>
        </div>

        <div className="flex items-center gap-6 mb-10">
           <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(article.authorName)}&background=random`} alt="" className="w-14 h-14 rounded-2xl border-2 border-white shadow-lg" />
           <div>
             <h1 className="text-4xl font-black text-slate-800 tracking-tighter leading-none mb-2">{article.title}</h1>
             <div className="flex items-center gap-4 text-xs font-bold text-slate-400 uppercase tracking-wider">
               <span>Por {article.authorName}</span>
               <span className="w-1.5 h-1.5 rounded-full bg-slate-200"></span>
               <span>{formatDate(article.updatedAt)}</span>
             </div>
           </div>
        </div>
      </div>

      <div className="p-10 pt-0 flex flex-col lg:flex-row gap-12">
        <div className="flex-1 prose prose-slate prose-lg max-w-none text-slate-700 leading-relaxed font-medium">
          {article.content.split('\n').map((line, i) => {
            const cleanLine = line.trim();
            
            // Correção: Ignora linhas totalmente vazias para evitar buracos no layout.
            // O espaçamento visual é controlado pela classe 'mb-4' nos parágrafos.
            if (!cleanLine) return null;

            // Formatação de Cabeçalhos
            if (cleanLine.startsWith('# ')) {
                return <h1 key={i} className="text-3xl font-black text-slate-900 mt-8 mb-4 tracking-tight uppercase">{cleanLine.replace('# ', '')}</h1>;
            }
            if (cleanLine.startsWith('## ')) {
                return <h2 key={i} className="2xl font-black text-slate-800 mt-6 mb-3 tracking-tight border-l-4 border-blue-600 pl-4">{cleanLine.replace('## ', '')}</h2>;
            }
            if (cleanLine.startsWith('### ')) {
                return <h3 key={i} className="xl font-bold text-slate-800 mt-5 mb-2">{cleanLine.replace('### ', '')}</h3>;
            }
            
            // Formatação de Listas
            if (cleanLine.startsWith('- ')) {
                return (
                  <div key={i} className="flex items-start gap-3 mb-2 ml-2">
                    <span className="text-blue-600 font-bold text-lg leading-none mt-1">•</span>
                    <span>{formatText(cleanLine.replace('- ', ''))}</span>
                  </div>
                );
            }
            
            // Parágrafo Padrão (com suporte a negrito)
            return <p key={i} className="mb-4 text-justify">{formatText(cleanLine)}</p>;
          })}
        </div>

        {article.attachments && article.attachments.length > 0 && (
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100 sticky top-8">
              <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-6 flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Recursos Anexos
              </h3>
              <div className="space-y-4">
                {article.attachments.map(att => (
                  <div key={att.id} onClick={() => setPreviewAttachment(att)} className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer group">
                    <div className="flex items-center gap-3 mb-3">
                       <div className="p-2 bg-slate-50 rounded-xl group-hover:bg-blue-50 transition-colors">
                          {getFileIcon(att.type)} {/* Updated to use new getFileIcon */}
                       </div>
                       <div className="flex-1 min-w-0">
                          <p className="text-xs font-black text-slate-800 truncate uppercase tracking-tighter" title={att.name}>{att.name}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{att.type.split('/')[1] || 'DOC'}</p>
                       </div>
                    </div>
                    <button onClick={(e) => handleDownload(e, att)} className="w-full py-2 bg-slate-50 text-[10px] font-black uppercase text-slate-500 rounded-xl hover:bg-slate-900 hover:text-white transition-all tracking-widest">Baixar</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {previewAttachment && (
         <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-xl p-8" onClick={() => setPreviewAttachment(null)}>
           <div className="bg-white w-full max-w-5xl h-full max-h-[85vh] rounded-[40px] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
             <div className="p-8 border-b border-slate-100 flex items-center justify-between">
               <div className="flex items-center gap-4">
                 <div className="p-3 bg-slate-50 rounded-2xl">{getFileIcon(previewAttachment.type)}</div>
                 <div>
                   <h3 className="font-black text-slate-800 text-xl tracking-tighter uppercase max-w-md truncate">{previewAttachment.name}</h3>
                   <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                       {previewAttachment.url.startsWith('data:') ? 'Arquivo Interno' : 'Arquivo Externo (WordPress)'}
                   </span>
                 </div>
               </div>
               <button onClick={() => setPreviewAttachment(null)} className="p-3 hover:bg-slate-100 rounded-full transition-colors text-slate-400"><X size={24} /></button>
             </div>
             
             <div className="flex-1 bg-slate-100 overflow-hidden flex items-center justify-center relative">
               {previewAttachment.type.includes('image') ? (
                 <img src={previewAttachment.url} alt="" className="max-w-full max-h-full object-contain p-8" />
               ) : previewAttachment.type.includes('pdf') ? (
                 <iframe 
                    src={getViewerUrl(previewAttachment)} 
                    className="w-full h-full border-none bg-white" 
                    title="PDF Viewer"
                 />
               ) : previewAttachment.type.includes('audio') ? (
                 <div className="flex flex-col items-center gap-6 p-12 bg-white rounded-3xl shadow-xl">
                   <div className="w-24 h-24 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center animate-pulse"><Music size={40} /></div>
                   <audio controls src={previewAttachment.url} className="w-80" />
                   <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">Player de Áudio Wiki</p>
                 </div>
               ) : previewAttachment.type.includes('video') ? (
                 <video controls src={previewAttachment.url} className="max-w-full max-h-full p-4" />
               ) : (
                 // Fallback for unsupported types, including Office docs (now download only)
                 <div className="text-center p-12 bg-white rounded-3xl shadow-xl">
                    {getFileIcon(previewAttachment.type)}
                    <p className="text-slate-500 font-bold mb-4 mt-4">
                      {isOfficeDoc(previewAttachment.type) ? 'Para visualizar arquivos do Office, por favor, faça o download.' : 'Pré-visualização não suportada para este formato.'}
                    </p>
                    <button onClick={(e) => handleDownload(e, previewAttachment)} className="px-8 py-3 bg-blue-600 text-white font-black rounded-2xl flex items-center gap-2 mx-auto">
                        <Download className="w-4 h-4" /> Baixar Arquivo
                    </button>
                 </div>
               )}
             </div>
           </div>
         </div>
       )}
    </div>
  );
};